import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FiAlertCircle, FiEye, FiDownload, FiEdit, FiAlertCircle as AlertCircleIcon, FiRefreshCw } from "react-icons/fi";
import { useState, useEffect, useMemo, useCallback } from "react";
import { gql, useReactiveVar } from "@apollo/client";
import { fulfillmentScreenQueryVar } from "./FulfillmentScreen";
import { useExportShippingLabelsScreenQuery, DeliveryMethod } from "../types";
import ErrorScreen from "./ErrorScreen";
import LoadingScreen from "./LoadingScreen";
import TextInput from "./TextInput";
import { showExportShippingLabelsVar } from "../helpers/client";
import { DateTime } from "luxon";
gql `
query ExportShippingLabelsScreen($first: Int!, $after: String, $query: FulfillmentQuery!, $businessId: ID!) {
  business(id: $businessId) {
    id
    name
  }
  fulfillments(first: $first, after: $after, query: $query) {
    edges {
      node {
        ...FulfillmentItem
      }
    }
    pageInfo {
      endCursor
    }
  }
}
`;
const PIRATE_SHIP_COLUMNS = [
    'Email', 'Name', 'Address', 'Address Line 2', 'City', 'State', 'Zipcode', 'Country', 'Order ID', 'Order Items', 'Pounds', 'Length', 'Width', 'Height'
];
const STAMPS_COM_COLUMNS = [
    'Order ID (required)', 'Order Date', 'Order Value', 'Requested Service', 'Ship To - Name',
    'Ship To - Company', 'Ship To - Address 1', 'Ship To - Address 2', 'Ship To - Address 3',
    'Ship To - State/Province', 'Ship To - City', 'Ship To - Postal Code', 'Ship To - Country',
    'Ship To - Phone', 'Ship To - Email', 'Total Weight in Oz', 'Dimensions - Length',
    'Dimensions - Width', 'Dimensions - Height', 'Notes - From Customer', 'Notes - Internal',
    'Gift Wrap?', 'Gift Message'
];
const SHIPPO_COLUMNS = [
    'Recipient name', 'Street line 1', 'Street line 2', 'Postal/Zip code', 'City', 'State', 'Country',
    'Recipient email', 'Phone number', 'Order number', 'Total order weight', 'Total order weight unit',
    'Order date', 'SKU Number'
];
const SHIP_STATION_COLUMNS = [
    'Order #', 'Order Date', 'Weight(oz)', 'Order Source', 'Notes from the Buyer', 'Buyer Email',
    'Recipient Full Name', 'Recipient Phone', 'Address Line 1', 'Address Line 2', 'City', 'State',
    'Postal Code', 'Country Code', 'Item SKU', 'Height(in)', 'Length(in)', 'Width(in)'
];
const EASYSHIP_COLUMNS = [
    "Choose Courier by",
    "Shipping Insurance",
    "Taxes & Duties Paid by*",
    "Platform",
    "Platform Order Number",
    "Order Tags",
    "Receiver's Full Name*",
    "Receiver's Phone Number*",
    "Receiver's Email*",
    "Receiver's Tax ID",
    "Receiver's Address Line 1*",
    "Receiver's Address Line 2",
    "Receiver's Postal Code*",
    "Receiver's City*",
    "Receiver's State/Province",
    "Receiver's Country*",
    "Item Length (in)*",
    "Item Width (in)*",
    "Item Height (in)*",
    "Item Weight (lb)*",
    "Item Category/HS codes*",
    "Item Contains Liquid",
    "Item Contains Battery",
    "Item Description*",
    "Item Country of Origin",
    "Item SKU",
    "Item Customs Value*",
    "Item Customs Value Currency*",
    "Item Quantity",
    "Buyer's Notes",
    "Seller's Notes",
];
// Update COLUMN_DEFINITIONS
const COLUMN_DEFINITIONS = {
    pirateship: PIRATE_SHIP_COLUMNS,
    shippo: SHIPPO_COLUMNS,
    shipstation: SHIP_STATION_COLUMNS,
    easyship: EASYSHIP_COLUMNS,
    "stamps.com": STAMPS_COM_COLUMNS,
    // Add more providers here as needed
};
const ExportShippingLabelsScreen = ({ businessId }) => {
    var _a;
    const fulfillmentScreenQuery = useReactiveVar(fulfillmentScreenQueryVar);
    let [businessName, setBusinessName] = useState("");
    let [first, setFirst] = useState(0);
    let [prevCursor, setPrevCursor] = useState(null);
    let [actuallyLoading, setActuallyLoading] = useState(false);
    let [fulfillments, setFulfillments] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState("pirateship");
    const [itemDimensions, setItemDimensions] = useState({});
    const [isGenerating, setIsGenerating] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [shippingRows, setShippingRows] = useState([]);
    const fetchAllFulfillments = useCallback(() => {
        setFulfillments([]);
        setPrevCursor(null);
        setActuallyLoading(true);
        setFirst(50);
    }, []);
    useEffect(() => {
        fetchAllFulfillments();
    }, [fetchAllFulfillments]);
    const { error } = useExportShippingLabelsScreenQuery({
        fetchPolicy: "no-cache",
        skip: !first,
        variables: {
            first: first,
            query: Object.assign(Object.assign({}, fulfillmentScreenQuery), { deliveryMethod: DeliveryMethod.Shipping, businessId }),
            after: prevCursor,
            businessId,
        },
        onCompleted: (data) => {
            var _a;
            if (actuallyLoading) {
                // Set business name on first load
                if (((_a = data === null || data === void 0 ? void 0 : data.business) === null || _a === void 0 ? void 0 : _a.name) && businessName === "") {
                    setBusinessName(data.business.name);
                }
                // Merge results
                if ((data === null || data === void 0 ? void 0 : data.fulfillments.edges.length) > 0)
                    setFulfillments(prev => [...prev, ...data.fulfillments.edges.map(edge => edge.node)]);
                // If there are more fulfillments, fetch them
                if ((data === null || data === void 0 ? void 0 : data.fulfillments.edges.length) > 0 && (data === null || data === void 0 ? void 0 : data.fulfillments.pageInfo.endCursor) != prevCursor) {
                    setPrevCursor(data === null || data === void 0 ? void 0 : data.fulfillments.pageInfo.endCursor);
                }
                // If we're done, set first back to 0
                else {
                    setActuallyLoading(false);
                    setFirst(0);
                    setPrevCursor(null);
                    setIsInitialLoad(false); // Set initial load to false after first complete load
                }
            }
        }
    });
    // Verify that all fulfillments or addons have a platformID, if not, keep a list of unique ones that need attention
    function dedupeArray(arr) {
        const seen = new Set();
        return arr.filter(item => {
            const key = `${item.type}|${item.description}|${item.tierName}`;
            return seen.has(key) ? false : seen.add(key);
        });
    }
    const missingItems = dedupeArray(fulfillments.flatMap(f => {
        var _a, _b;
        const missingVariants = ((_a = f.productSelections) === null || _a === void 0 ? void 0 : _a.flatMap(ps => {
            if (!ps.variant)
                return [];
            return ps.variant.platformId
                ? []
                : [{ type: 'Option', description: ps.variant.description, tierName: f.membership.tier.name, benefitId: f.benefit.id }];
        })) || [];
        const missingAddons = ((_b = f.addons) === null || _b === void 0 ? void 0 : _b.filter(a => !a.platformId).map(a => ({ type: 'Addon', description: a.description, tierName: f.membership.tier.name, benefitId: f.benefit.id }))) || [];
        return [...missingVariants, ...missingAddons];
    }));
    const hasMissingItems = missingItems.length > 0;
    // Get all the unique platformIds from the fulfillments
    // Memoize platformIds to prevent unnecessary recalculations
    const platformItems = useMemo(() => {
        return fulfillments.flatMap((f) => {
            var _a, _b;
            const variantItems = ((_a = f.productSelections) === null || _a === void 0 ? void 0 : _a.flatMap((ps) => {
                if (!ps.variant || !ps.variant.platformId || ps.variant.platformId === "SKIP")
                    return [];
                return [{
                        type: 'Option',
                        platformId: ps.variant.platformId,
                        description: ps.variant.description,
                        tierName: f.membership.tier.name,
                        benefitId: f.benefit.id
                    }];
            })) || [];
            const addonItems = ((_b = f.addons) === null || _b === void 0 ? void 0 : _b.flatMap((addon) => {
                if (!addon.platformId || addon.platformId === "SKIP")
                    return [];
                return [{
                        type: 'Addon',
                        platformId: addon.platformId,
                        description: addon.description,
                        tierName: f.membership.tier.name,
                        benefitId: f.benefit.id
                    }];
            })) || [];
            return [...variantItems, ...addonItems];
        });
    }, [fulfillments]);
    const platformIds = useMemo(() => {
        return Array.from(new Set(platformItems.map(item => item.platformId)));
    }, [platformItems]);
    // Initialize weights only when fulfillments change
    useEffect(() => {
        const initialDimensions = platformIds.reduce((acc, id) => {
            if (!(id in itemDimensions)) {
                acc[id] = {};
            }
            else {
                acc[id] = itemDimensions[id];
            }
            return acc;
        }, {});
        setItemDimensions(initialDimensions);
    }, [platformIds]);
    const handleProviderChange = (e) => {
        setSelectedProvider(e.target.value);
        setIsPreviewOpen(false);
    };
    const handleDimensionChange = (e, item, dimension) => {
        const target = e.target; // Type assertion
        const value = parseFloat(target.value);
        setItemDimensions(prev => (Object.assign(Object.assign({}, prev), { [item]: Object.assign(Object.assign({}, prev[item]), { [dimension]: value }) })));
    };
    const calculatePackageDimensions = (productIds) => {
        const dimensions = productIds.map(id => itemDimensions[id] || {});
        // Use maximum length and width, sum heights
        const maxLength = Math.max(...dimensions.map(d => d.length || 0));
        const maxWidth = Math.max(...dimensions.map(d => d.width || 0));
        const totalHeight = dimensions.reduce((sum, d) => sum + (d.height || 0), 0);
        const totalWeight = dimensions.reduce((sum, d) => sum + (d.weight || 0), 0);
        return {
            length: maxLength.toFixed(2),
            width: maxWidth.toFixed(2),
            height: totalHeight.toFixed(2),
            weight: totalWeight.toFixed(2),
            weightOz: (totalWeight * 16).toFixed(2)
        };
    };
    function groupFulfillmentsByCustomerAndDate(fulfillments) {
        const map = new Map();
        fulfillments.forEach((f) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
            // We group by user email, the YYYY-MM-DD date, and address
            const date = DateTime.fromISO(f.fulfillmentDate).toISODate();
            const userEmail = f.membership.user.emailAddress || 'missing-email';
            const addressKey = [
                (_b = (_a = f.address.shippingName) === null || _a === void 0 ? void 0 : _a.trim()) === null || _b === void 0 ? void 0 : _b.toLowerCase(),
                (_d = (_c = f.address.line1) === null || _c === void 0 ? void 0 : _c.trim()) === null || _d === void 0 ? void 0 : _d.toLowerCase(),
                (_f = (_e = f.address.line2) === null || _e === void 0 ? void 0 : _e.trim()) === null || _f === void 0 ? void 0 : _f.toLowerCase(),
                (_h = (_g = f.address.city) === null || _g === void 0 ? void 0 : _g.trim()) === null || _h === void 0 ? void 0 : _h.toLowerCase(),
                (_k = (_j = f.address.state) === null || _j === void 0 ? void 0 : _j.trim()) === null || _k === void 0 ? void 0 : _k.toLowerCase(),
                (_m = (_l = f.address.zip) === null || _l === void 0 ? void 0 : _l.trim()) === null || _m === void 0 ? void 0 : _m.toLowerCase(),
                (_p = (_o = f.address.country) === null || _o === void 0 ? void 0 : _o.trim()) === null || _p === void 0 ? void 0 : _p.toLowerCase(),
            ].join('|');
            const groupingKey = `${userEmail}|${date}|${addressKey}`;
            if (!map.has(groupingKey)) {
                map.set(groupingKey, []);
            }
            map.get(groupingKey).push(f);
        });
        return map;
    }
    const generateShippingRows = () => {
        // 1. Group by customer + date + address
        const grouped = groupFulfillmentsByCustomerAndDate(fulfillments);
        const shippingRowsArray = [];
        // 2. For each group of fulfillments, combine them into a single row
        grouped.forEach((groupedFulfillments) => {
            // Gather all SKUs from the group
            const productIds = groupedFulfillments.flatMap((f) => {
                var _a, _b;
                const vIds = ((_a = f.productSelections) === null || _a === void 0 ? void 0 : _a.flatMap((ps) => {
                    var _a;
                    if (!((_a = ps.variant) === null || _a === void 0 ? void 0 : _a.platformId) || ps.variant.platformId === "SKIP")
                        return [];
                    return [ps.variant.platformId];
                })) || [];
                const aIds = ((_b = f.addons) === null || _b === void 0 ? void 0 : _b.flatMap((addon) => {
                    if (!addon.platformId || addon.platformId === "SKIP")
                        return [];
                    return [addon.platformId];
                })) || [];
                return [...vIds, ...aIds];
            });
            // Skip this group if there are no valid product IDs after filtering
            if (productIds.length === 0)
                return;
            // For the address, we can take from the first one in the group (assuming same address)
            const anyF = groupedFulfillments[0];
            // Summation of dimensional info, etc.
            const { length, width, height, weight, weightOz } = (() => {
                // For each unique productId in the entire group, sum or max dimension
                const uniqueIds = Array.from(new Set(productIds));
                const dimensionItems = uniqueIds.map((id) => itemDimensions[id] || {});
                const totalWeight = dimensionItems.reduce((sum, d) => sum + (d.weight || 0), 0);
                const totalHeight = dimensionItems.reduce((sum, d) => sum + (d.height || 0), 0);
                const maxLength = Math.max(...dimensionItems.map((d) => d.length || 0));
                const maxWidth = Math.max(...dimensionItems.map((d) => d.width || 0));
                return {
                    length: maxLength.toFixed(2),
                    width: maxWidth.toFixed(2),
                    height: totalHeight.toFixed(2),
                    weight: totalWeight.toFixed(2),
                    weightOz: (totalWeight * 16).toFixed(2),
                };
            })();
            // Combine order IDs & shipping notes
            const combinedOrderIds = groupedFulfillments.length === 1
                ? groupedFulfillments[0].id
                : groupedFulfillments
                    .filter(f => f.benefit.__typename !== "WelcomeGiftBenefit")
                    .map(f => f.id)
                    .join(', ');
            const combinedNotes = groupedFulfillments
                .map((f) => f.shippingNotes || '')
                .filter(Boolean)
                .join(' | ');
            // Since all items are on the same date, pick one date (assuming they share the same)
            const orderDate = DateTime.fromISO(anyF.fulfillmentDate);
            // Build the row for the active provider
            const row = (() => {
                switch (selectedProvider) {
                    case 'pirateship':
                        return {
                            Email: anyF.membership.user.emailAddress || '',
                            Name: anyF.address.shippingName ||
                                `${anyF.membership.user.firstName} ${anyF.membership.user.lastName}`,
                            Address: anyF.address.line1,
                            'Address Line 2': anyF.address.line2 || '',
                            City: anyF.address.city,
                            State: anyF.address.state,
                            Zipcode: anyF.address.zip,
                            Country: anyF.address.country || 'US',
                            'Order ID': combinedOrderIds,
                            'Order Items': productIds.join(', '),
                            Pounds: weight,
                            Length: length,
                            Width: width,
                            Height: height,
                        };
                    case 'stamps.com':
                        return {
                            'Order ID (required)': combinedOrderIds,
                            'Order Date': orderDate.toFormat("MM/dd/yyyy"),
                            'Order Value': "",
                            'Requested Service': "Standard Shipping",
                            'Ship To - Name': anyF.address.shippingName || `${anyF.membership.user.firstName} ${anyF.membership.user.lastName}`,
                            'Ship To - Company': null,
                            'Ship To - Address 1': anyF.address.line1,
                            'Ship To - Address 2': anyF.address.line2 || '',
                            'Ship To - Address 3': null,
                            'Ship To - State/Province': anyF.address.state,
                            'Ship To - City': anyF.address.city,
                            'Ship To - Postal Code': anyF.address.zip,
                            'Ship To - Country': anyF.address.country || 'US',
                            'Ship To - Phone': anyF.membership.user.phoneNumber || '',
                            'Ship To - Email': anyF.membership.user.emailAddress || '',
                            'Total Weight in Oz': weightOz,
                            'Dimensions - Length': length,
                            'Dimensions - Width': width,
                            'Dimensions - Height': height,
                            'Notes - From Customer': combinedNotes,
                            'Notes - Internal': null,
                            'Gift Wrap?': false,
                            'Gift Message': null
                        };
                    case 'shippo':
                        return {
                            'Recipient name': anyF.address.shippingName ||
                                `${anyF.membership.user.firstName} ${anyF.membership.user.lastName}`,
                            'Street line 1': anyF.address.line1,
                            'Street line 2': anyF.address.line2 || '',
                            'Postal/Zip code': anyF.address.zip || '00000',
                            City: anyF.address.city,
                            State: anyF.address.state,
                            Country: anyF.address.country || 'US',
                            'Recipient email': anyF.membership.user.emailAddress || '',
                            'Phone number': anyF.membership.user.phoneNumber || '',
                            'Order number': combinedOrderIds,
                            'Total order weight': weight,
                            'Total order weight unit': 'lb',
                            'Order date': orderDate.toISODate(),
                            'SKU Number': productIds.join(', '),
                        };
                    case 'shipstation':
                        return {
                            'Order #': combinedOrderIds,
                            'Order Date': orderDate.toISODate(),
                            'Weight(oz)': weightOz,
                            'Order Source': 'Withfriends',
                            'Notes from the Buyer': combinedNotes,
                            'Buyer Email': anyF.membership.user.emailAddress || '',
                            'Recipient Full Name': anyF.address.shippingName ||
                                `${anyF.membership.user.firstName} ${anyF.membership.user.lastName}`,
                            'Recipient Phone': anyF.membership.user.phoneNumber || '',
                            'Address Line 1': anyF.address.line1,
                            'Address Line 2': anyF.address.line2 || '',
                            City: anyF.address.city,
                            State: anyF.address.state,
                            'Postal Code': anyF.address.zip,
                            'Country Code': anyF.address.country || 'US',
                            'Item SKU': productIds.join(', '),
                            'Height(in)': height,
                            'Length(in)': length,
                            'Width(in)': width,
                        };
                    case 'easyship':
                        return {
                            "Choose Courier by": "",
                            "Shipping Insurance": "",
                            "Taxes & Duties Paid by*": "",
                            "Platform": "Withfriends",
                            "Platform Order Number": combinedOrderIds,
                            "Order Tags": "",
                            "Receiver's Full Name*": anyF.address.shippingName || `${anyF.membership.user.firstName} ${anyF.membership.user.lastName}`,
                            "Receiver's Phone Number*": anyF.membership.user.phoneNumber || '',
                            "Receiver's Email*": anyF.membership.user.emailAddress || '',
                            "Receiver's Tax ID": "",
                            "Receiver's Address Line 1*": anyF.address.line1,
                            "Receiver's Address Line 2": anyF.address.line2 || '',
                            "Receiver's Postal Code*": anyF.address.zip,
                            "Receiver's City*": anyF.address.city,
                            "Receiver's State/Province": anyF.address.state,
                            "Receiver's Country*": anyF.address.country || 'US',
                            "Item Length (in)*": length,
                            "Item Width (in)*": width,
                            "Item Height (in)*": height,
                            "Item Weight (lb)*": weight,
                            "Item Category/HS codes*": "",
                            "Item Contains Liquid": "",
                            "Item Contains Battery": "",
                            "Item Description*": "",
                            "Item Country of Origin": "",
                            "Item SKU": productIds.join(', '),
                            "Item Customs Value*": "",
                            "Item Customs Value Currency*": "",
                            "Item Quantity": productIds.length.toString(),
                            "Buyer's Notes": combinedNotes,
                            "Seller's Notes": ""
                        };
                    default:
                        throw new Error(`Unsupported provider: ${selectedProvider}`);
                }
            })();
            shippingRowsArray.push(row);
        });
        return shippingRowsArray;
    };
    const handleGenerateAndDownload = () => {
        setIsPreviewOpen(false);
        setIsGenerating(true);
        const rows = generateShippingRows();
        setShippingRows(rows);
        setTimeout(() => {
            setIsGenerating(false);
            setIsPreviewOpen(true);
        }, 200);
    };
    const handleDownloadCSV = () => {
        if (!selectedProvider) {
            console.error("No provider selected");
            return;
        }
        const columns = COLUMN_DEFINITIONS[selectedProvider];
        if (!columns) {
            console.error(`No column definition found for provider: ${selectedProvider}`);
            return;
        }
        // Helper function to escape CSV values
        const escapeCSV = (value) => {
            if (value === null || value === undefined)
                return '';
            const stringValue = String(value);
            // If value contains comma, quotes, or newlines, wrap in quotes and escape existing quotes
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
        };
        const csvString = columns.join(",") +
            "\n" +
            shippingRows
                .map(row => columns.map(col => escapeCSV(row[col])).join(","))
                .join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${businessName}_${selectedProvider}_shipping_labels.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up object URL
        URL.revokeObjectURL(url);
    };
    if (isInitialLoad && actuallyLoading)
        return _jsx(LoadingScreen, { loadingMessage: "Loading your fulfillments..." });
    if (error)
        return _jsx(ErrorScreen, { message: error.message });
    const isFormValid = selectedProvider &&
        Object.values(itemDimensions).every((dims) => dims.weight !== undefined);
    return (_jsxs("div", { className: "max-w-full p-6", children: [_jsx("div", { className: "flex justify-between items-center mb-4", children: _jsx("h2", { className: "text-xl font-heavy font-bold text-salmon-600", children: "Export Shipping Labels" }) }), actuallyLoading ? (_jsx(LoadingScreen, { loadingMessage: "Refreshing your fulfillments..." })) : (hasMissingItems ? (_jsxs("div", { className: "mb-4 p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700", children: [_jsxs("div", { className: "flex items-center", children: [_jsx(AlertCircleIcon, { className: "mr-2" }), _jsx("p", { className: "font-bold", children: "Warning" })] }), _jsx("p", { className: "mt-2", children: "Some items are missing product identifiers. Please fix these and return here:" }), _jsx("ul", { className: "list-disc list-inside mt-2", children: missingItems.map((item, index) => (_jsxs("li", { className: "flex justify-between items-center mb-1", children: [_jsxs("div", { children: [item.type, " (Name: ", item.description, ") in tier: ", item.tierName] }), _jsx("a", { href: `${process.env.REACT_APP_WITHFRIENDS_HOST}/benefit/${item.benefitId}/change_subscription_benefit/modal:is_standalone`, target: "_blank", rel: "noopener noreferrer", className: "text-salmon-600 hover:text-salmon-700", children: _jsx(FiEdit, { size: 16, className: "w-4 h-4 text-current" }) })] }, index))) }), _jsxs("button", { onClick: fetchAllFulfillments, className: "mt-4 mb-4 bg-gray-200 text-gray-700 px-3 py-1 flex items-center", children: [_jsx(FiRefreshCw, { size: 16, className: "mr-2" }), "Refresh"] })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mb-4", children: [_jsx("label", { className: "block text-sm font-medium text-wfGray-800", children: "Label Provider Format" }), _jsxs("select", { value: selectedProvider, onChange: handleProviderChange, className: "mt-1 block w-full p-2 border border-wfGray-300 focus:border-salmon-600 focus:border-2", children: [_jsx("option", { value: "", children: "Select a provider" }), _jsx("option", { value: "pirateship", children: "PirateShip CSV" }), _jsx("option", { value: "shippo", children: "Shippo CSV" }), _jsx("option", { value: "shipstation", children: "ShipStation CSV" }), _jsx("option", { value: "easyship", children: "Easyship CSV" }), _jsx("option", { value: "stamps.com", children: "Stamps.com CSV" })] })] }), _jsxs("div", { className: "mb-4", children: [_jsx("label", { className: "block text-sm font-medium text-gray-700", children: "Item Dimensions" }), platformIds.map((item) => {
                                var _a, _b, _c, _d;
                                return (_jsxs("div", { className: "mt-2 border-b pb-4", children: [_jsx("label", { className: "block text-sm text-gray-500", children: item }), _jsxs("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-4", children: [_jsxs("div", { children: [_jsxs("label", { className: "block text-xs text-gray-400", children: ["Weight (lbs) ", _jsx("span", { className: "text-red-500", children: "*" })] }), _jsx(TextInput, { isNumber: true, value: (_a = itemDimensions[item]) === null || _a === void 0 ? void 0 : _a.weight, onChange: (e) => handleDimensionChange(e, item, 'weight'), placeholder: "Weight", className: "mt-1 block w-full p-2 border border-wfGray-300 focus:border-salmon-600 focus:border-2" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs text-gray-400", children: "Length (in)" }), _jsx(TextInput, { isNumber: true, value: (_b = itemDimensions[item]) === null || _b === void 0 ? void 0 : _b.length, onChange: (e) => handleDimensionChange(e, item, 'length'), placeholder: "Length", className: "mt-1 block w-full p-2 border border-wfGray-300 focus:border-salmon-600 focus:border-2" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs text-gray-400", children: "Width (in)" }), _jsx(TextInput, { isNumber: true, value: (_c = itemDimensions[item]) === null || _c === void 0 ? void 0 : _c.width, onChange: (e) => handleDimensionChange(e, item, 'width'), placeholder: "Width", className: "mt-1 block w-full p-2 border border-wfGray-300 focus:border-salmon-600 focus:border-2" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-xs text-gray-400", children: "Height (in)" }), _jsx(TextInput, { isNumber: true, value: (_d = itemDimensions[item]) === null || _d === void 0 ? void 0 : _d.height, onChange: (e) => handleDimensionChange(e, item, 'height'), placeholder: "Height", className: "mt-1 block w-full p-2 border border-wfGray-300 focus:border-salmon-600 focus:border-2" })] })] }), _jsx("div", { className: "text-sm text-gray-400", children: platformItems
                                                .filter(platformItem => platformItem.platformId === item)
                                                .reduce((acc, curr) => {
                                                const key = `${curr.type.toLowerCase()}s for tier <strong>${curr.tierName}</strong>`;
                                                if (!acc.some(entry => entry.text === key)) {
                                                    acc.push({
                                                        text: key,
                                                        tierName: curr.tierName,
                                                        benefitId: curr.benefitId
                                                    });
                                                }
                                                return acc;
                                            }, [])
                                                .map(({ text, tierName, benefitId }, i) => (_jsxs("div", { className: "flex justify-between items-center mb-1", children: [_jsx("div", { dangerouslySetInnerHTML: { __html: `found in ${text}` } }), _jsx("a", { href: `${process.env.REACT_APP_WITHFRIENDS_HOST}/benefit/${benefitId}/change_subscription_benefit/modal:is_standalone`, className: "text-salmon-600 hover:text-salmon-700", target: "_blank", rel: "noopener noreferrer", children: _jsx(FiEdit, { size: 16, className: "w-4 h-4 text-current" }) })] }, i))) })] }, item));
                            })] }), _jsxs("button", { onClick: fetchAllFulfillments, className: "mt-4 mb-4 bg-gray-200 text-gray-700 px-3 py-1 flex items-center", children: [_jsx(FiRefreshCw, { size: 16, className: "mr-2" }), "Refresh"] }), _jsx("button", { onClick: handleGenerateAndDownload, disabled: !isFormValid || isGenerating, className: `w-full ${isFormValid && !isGenerating
                            ? "bg-salmon-600 hover:bg-salmon-700"
                            : "bg-gray-300"} text-white px-4 py-2 flex items-center justify-center`, children: isGenerating ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "animate-spin h-5 w-5 border-b-2 border-white mr-2" }), "Generating..."] })) : (_jsxs(_Fragment, { children: [_jsx(FiDownload, { className: "mr-2" }), "Generate and Preview"] })) }), isPreviewOpen && (_jsxs("div", { className: "mt-4 border border-wfGray-300 p-4", children: [_jsx("h3", { className: "text-lg font-semibold mb-2", children: "Preview" }), _jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "w-full text-sm", children: [_jsx("thead", { children: _jsx("tr", { children: (_a = COLUMN_DEFINITIONS[selectedProvider]) === null || _a === void 0 ? void 0 : _a.map((col) => (_jsx("th", { className: "border p-1 whitespace-nowrap", children: col }, col))) }) }), _jsx("tbody", { children: shippingRows.map((row, index) => {
                                                var _a;
                                                return (_jsx("tr", { children: (_a = COLUMN_DEFINITIONS[selectedProvider]) === null || _a === void 0 ? void 0 : _a.map((col) => (_jsx("td", { className: "border p-1 whitespace-nowrap", children: row[col] }, col))) }, index));
                                            }) })] }) }), _jsxs("div", { className: "mt-4 flex justify-between", children: [_jsxs("button", { onClick: () => setIsPreviewOpen(false), className: "text-salmon-600 hover:underline flex items-center", children: [_jsx(FiEye, { className: "mr-1", size: 16 }), "Close Preview"] }), _jsx("button", { onClick: handleDownloadCSV, className: "bg-salmon-600 hover:bg-salmon-700 text-white font-bold py-2 px-4", children: "Download CSV" })] })] })), _jsxs("div", { className: "mt-8 bg-gray-100 p-4", children: [_jsx("h3", { className: "text-lg font-semibold mb-2", children: "How It Works" }), _jsxs("ul", { className: "list-disc pl-5 space-y-2", children: [_jsx("li", { children: "Before starting, create or update the product IDs in your subscription boxes and welcome gifts" }), _jsx("li", { children: "Select your preferred label provider format" }), _jsx("li", { children: "Enter the weights for each product ID. When there are mutiple products in a shipment, we add the weights together." }), _jsx("li", { children: "Optionally, add the dimensions for each product ID. When there are mutiple products in a shipment, we use the largest dimensions for width and length, and add the heights together." }), _jsx("li", { children: "Click \"Generate and Preview\" to see your data" }), _jsx("li", { children: "Review the preview and click \"Download CSV\" when ready" })] })] }), _jsxs("div", { className: "mt-4 flex items-start space-x-4", children: [_jsx("div", { className: "flex-shrink-0", children: _jsx(FiAlertCircle, { className: "h-6 w-6 text-yellow-500" }) }), _jsxs("div", { children: [_jsx("h4", { className: "text-lg font-semibold", children: "Need Help?" }), _jsx("p", { className: "mt-1 text-gray-600", children: "Contact our support team at support@withfriends.co for assistance with exporting shipping labels." })] })] })] }))), _jsx("div", { className: "mt-4 flex justify-end", children: _jsx("button", { className: "border border-black bg-white text-black font-bold py-2 px-4 mr-2", onClick: () => showExportShippingLabelsVar(false), children: "Back" }) })] }));
};
export default ExportShippingLabelsScreen;
